/**
 * The household sync wire protocol.
 *
 * Deliberately tiny: a one-byte tag followed by an opaque payload. Only two message types
 * carry CRDT data, and both treat it as bytes, which is what keeps every Yjs import inside
 * packages/core/src/yjs. Neither the client transport nor the Durable Object needs to know
 * what a state vector actually contains.
 *
 * The exchange is the standard two-way state-vector diff:
 *
 *   client -> STATE_VECTOR(sv_client)
 *   server -> UPDATE(everything the client is missing)
 *   server -> STATE_VECTOR(sv_server)
 *   server -> SYNCED
 *   client -> UPDATE(everything the server is missing)
 *
 * After that both sides push an UPDATE whenever their local document changes. Because the
 * payloads are Yjs updates, order and duplication do not matter: applying the same update
 * twice, or out of order, converges to the same document.
 */
export const SyncMessageType = {
  /** Payload: encoded state vector. */
  StateVector: 1,
  /** Payload: encoded document update. */
  Update: 2,
  /** Payload: empty. Server has sent everything the client was missing. */
  Synced: 3,
  /** Payload: UTF-8 JSON `SyncErrorPayload`. */
  Error: 4,
  /** Payload: UTF-8 JSON `SyncInfoPayload`, sent once on connect. */
  Info: 5,
} as const;

export type SyncMessageType = (typeof SyncMessageType)[keyof typeof SyncMessageType];

export interface SyncErrorPayload {
  code:
    | 'unauthorized'
    | 'forbidden'
    | 'read_only'
    | 'household_deleted'
    | 'rate_limited'
    | 'invalid_update'
    | 'quota_exceeded'
    | 'recipe_ceiling'
    | 'subscription_lapsed'
    | 'internal';
  message: string;
  /** Present for household_deleted, so the client can show the restore deadline. */
  purgeAfter?: number;
  /** Present for quota_exceeded and recipe_ceiling: the cap and the count that breached it. */
  limit?: number;
  count?: number;
}

/**
 * The household's plan, as the wire sees it.
 *
 * Structurally `Entitlement` from `@platewell/core`, declared again here because this
 * package depends only on zod — the same duplication `HouseholdRole` already has.
 */
export interface EntitlementPayload {
  plan: 'free' | 'pro' | 'locked';
  /** null means unlimited. */
  recipeLimit: number | null;
  source: 'none' | 'revenuecat' | 'complimentary';
  updatedAt: number;
}

export interface SyncInfoPayload {
  householdId: string;
  role: 'owner' | 'editor' | 'viewer';
  /** Documents below this schema version must not be written to; the client drops to read-only. */
  minWriterVersion: number;
  /** Authoritative clock, used to clamp displayed timestamps on skewed devices. */
  serverTime: number;
  docVersion: number;
  /**
   * The server's own cached entitlement, never the document's copy.
   *
   * The meta write goes through the 2–10 second debounced save path, so a household that
   * has just subscribed would otherwise sit locked for another few seconds. This arrives
   * on connect, immediately.
   */
  entitlement: EntitlementPayload;
}

export interface DecodedSyncMessage {
  type: SyncMessageType;
  payload: Uint8Array;
}

export function encodeSyncMessage(
  type: SyncMessageType,
  payload?: Uint8Array,
): Uint8Array<ArrayBuffer> {
  const body = payload ?? new Uint8Array(0);
  const frame = new Uint8Array(body.length + 1);
  frame[0] = type;
  frame.set(body, 1);
  return frame;
}

export function decodeSyncMessage(data: ArrayBuffer | Uint8Array): DecodedSyncMessage | null {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (bytes.length < 1) return null;
  const type = bytes[0] as SyncMessageType;
  if (!Object.values(SyncMessageType).includes(type)) return null;
  return { type, payload: bytes.subarray(1) };
}

export function encodeJsonMessage(type: SyncMessageType, value: unknown): Uint8Array {
  return encodeSyncMessage(type, new TextEncoder().encode(JSON.stringify(value)));
}

export function decodeJsonPayload<T>(payload: Uint8Array): T {
  return JSON.parse(new TextDecoder().decode(payload)) as T;
}
